import React from "react";
import { Box, IconButton, useMediaQuery } from "@mui/material";
import {
  CloseRounded,
  ArrowBackRounded,
  AddCommentRounded,
  LogoutRounded,
  OpenInNewRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../hooks/sharedHooks";
import { startNewChat, setMachineContext, closePanel } from "../chatBotSlice";
import MidasLogo from "../../../customer/midas/components/MidasLogo";
import { dt } from "../../themes/redesignTokens";

// ===================================================================
//  MIDAS Design Tokens (local subset)
// ===================================================================

const midas = {
  charcoal: "#1E1E2A",
  slate: "#2D2D3F",
  gold: "#D4A017",
};


// ===================================================================
//  ChatPanelHeader
// ===================================================================

interface ChatPanelHeaderProps {
  onClose: () => void;
}

const ChatPanelHeader: React.FC<ChatPanelHeaderProps> = ({ onClose }) => {
  const { translate } = useTranslation("chatBot");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const messages = useAppSelector((state) => state.chatBot.messages);
  const machineContext = useAppSelector((state) => state.chatBot.machineContext);
  const machineContextLoading = useAppSelector(
    (state) => state.chatBot.machineContextLoading
  );
  const isPhone = useMediaQuery("(max-width:768px)");
  const hasMessages = messages.length > 0;
  const inMachineMode = !!machineContext;

  const handleExitMachineMode = () => {
    if (machineContextLoading) return;
    dispatch(setMachineContext(null));
  };

  return (
    <Box
      sx={{
        height: "56px",
        background: `linear-gradient(135deg, ${midas.charcoal} 0%, ${midas.slate} 100%)`,
        padding: "0 16px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        borderRadius: isPhone ? "0" : "16px 16px 0 0",
        flexShrink: 0,
      }}
    >
      {/* Back arrow (phone only) */}
      {isPhone && (
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "rgba(255,255,255,0.6)",
            padding: "4px",
            "&:hover": { color: "#FFFFFF" },
          }}
          aria-label={translate("close")}
        >
          <ArrowBackRounded sx={{ fontSize: "20px" }} />
        </IconButton>
      )}

      {/* MIDAS Logo */}
      <MidasLogo size="md" />

      {/* Bot name + optional machine indicator */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "15px",
            fontWeight: 700,
            color: "#FFFFFF",
            letterSpacing: "0.5px",
          }}
        >
          {translate("botName")}
        </Box>
        {inMachineMode && (
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 500,
              color: midas.gold,
              letterSpacing: "0.3px",
              opacity: 0.9,
            }}
          >
            &middot; {machineContext.model}
          </Box>
        )}
      </Box>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Exit machine mode button — only visible in machine mode */}
      {inMachineMode && (
        <IconButton
          onClick={handleExitMachineMode}
          disabled={machineContextLoading}
          size="small"
          sx={{
            color: midas.gold,
            padding: "6px",
            borderRadius: "6px",
            opacity: 0.8,
            "&:hover": {
              color: midas.gold,
              opacity: 1,
              backgroundColor: "rgba(212, 160, 23, 0.15)",
            },
          }}
          aria-label={translate("machineMode.exit")}
          title={translate("machineMode.exit")}
        >
          <LogoutRounded sx={{ fontSize: "18px" }} />
        </IconButton>
      )}

      {/* New Chat button — only visible when there are messages */}
      {hasMessages && (
        <IconButton
          onClick={() => dispatch(startNewChat())}
          size="small"
          sx={{
            color: "rgba(255,255,255,0.6)",
            padding: "6px",
            borderRadius: "6px",
            "&:hover": {
              color: "#FFFFFF",
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
          aria-label={translate("newChat")}
          title={translate("newChat")}
        >
          <AddCommentRounded sx={{ fontSize: "18px" }} />
        </IconButton>
      )}

      {/* Open full page button */}
      <IconButton
        onClick={() => {
          dispatch(closePanel());
          navigate("/midas");
        }}
        size="small"
        sx={{
          color: "rgba(255,255,255,0.6)",
          padding: "6px",
          borderRadius: "6px",
          "&:hover": {
            color: "#FFFFFF",
            backgroundColor: "rgba(255,255,255,0.1)",
          },
        }}
        aria-label={translate("midas.openFullPage")}
        title={translate("midas.openFullPage")}
      >
        <OpenInNewRounded sx={{ fontSize: "18px" }} />
      </IconButton>

      {/* Close button */}
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          color: "rgba(255,255,255,0.6)",
          padding: "6px",
          borderRadius: "6px",
          "&:hover": {
            color: "#FFFFFF",
            backgroundColor: "rgba(255,255,255,0.1)",
          },
        }}
        aria-label={translate("close")}
      >
        <CloseRounded sx={{ fontSize: "20px" }} />
      </IconButton>
    </Box>
  );
};

export default ChatPanelHeader;
